import { supabase } from '../../../shared/lib/supabase';
import type {
  BillingPlan,
  BillingPlanCreateInput,
  BillingPlanEntitlement,
  BillingPlanEntitlementCreateInput,
  BillingPlanEntitlementUpdateInput,
  BillingPlanUpdateInput,
} from './billingTypes';

export class BillingPlansService {
  private plansTable = 'billing_plans';
  private entitlementsTable = 'billing_plan_entitlements';

  async getAll(): Promise<BillingPlan[]> {
    const { data, error } = await supabase
      .from(this.plansTable)
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching billing plans:', error);
      throw error;
    }

    return data || [];
  }

  async getById(id: string): Promise<BillingPlan | null> {
    const { data, error } = await supabase
      .from(this.plansTable)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching billing plan:', error);
      throw error;
    }

    return data;
  }

  async create(input: BillingPlanCreateInput): Promise<BillingPlan> {
    const payload: BillingPlanCreateInput = {
      cadence: 'monthly',
      billing_timezone: 'UTC',
      cycle_anchor_day: 1,
      currency: 'INR',
      base_subscription_fee: 0,
      is_active: true,
      valid_from: new Date().toISOString(),
      ...input,
    };

    const { data, error } = await supabase.from(this.plansTable).insert([payload]).select().single();

    if (error) {
      console.error('Error creating billing plan:', error);
      throw error;
    }

    return data;
  }

  async update(id: string, input: BillingPlanUpdateInput): Promise<BillingPlan> {
    const { data, error } = await supabase
      .from(this.plansTable)
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating billing plan:', error);
      throw error;
    }

    return data;
  }

  async getEntitlementsByPlanId(planId: string): Promise<BillingPlanEntitlement[]> {
    const { data, error } = await supabase
      .from(this.entitlementsTable)
      .select('*')
      .eq('billing_plan_id', planId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching billing plan entitlements:', error);
      throw error;
    }

    return data || [];
  }

  async createEntitlement(input: BillingPlanEntitlementCreateInput): Promise<BillingPlanEntitlement> {
    const payload: BillingPlanEntitlementCreateInput = {
      carryover_enabled: false,
      carryover_cap_type: null,
      carryover_cap_value: null,
      carryover_expiry_cycles: 1,
      carryover_consumption_order: 'carryover_first',
      ...input,
    };

    const { data, error } = await supabase.from(this.entitlementsTable).insert([payload]).select().single();

    if (error) {
      console.error('Error creating billing entitlement:', error);
      throw error;
    }

    return data;
  }

  async updateEntitlement(id: string, input: BillingPlanEntitlementUpdateInput): Promise<BillingPlanEntitlement> {
    const { data, error } = await supabase
      .from(this.entitlementsTable)
      .update({
        ...input,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating billing entitlement:', error);
      throw error;
    }

    return data;
  }

  async deleteEntitlement(id: string): Promise<void> {
    const { error } = await supabase.from(this.entitlementsTable).delete().eq('id', id);
    if (error) {
      console.error('Error deleting billing plan entitlement:', error);
      throw error;
    }
  }

  async deletePlan(id: string): Promise<void> {
    const { error } = await supabase.from(this.plansTable).delete().eq('id', id);
    if (error) {
      console.error('Error deleting billing plan:', error);
      throw error;
    }
  }
}

export const billingPlansService = new BillingPlansService();
